/** @jsxRuntime classic */
/** @jsx jsx */

import { Fragment } from 'react';
import { css, jsx } from '@emotion/react';
import { Colors, Heading, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  EventAction,
  InteractionName,
  SectionName,
  useAvroTracking,
} from '../../../observability';
import { MEDIA_MAX_WIDTH } from '../../../constants';
import { BackgroundColor } from './types';
import { useAnimationsSectionContext } from './Context/Context';

export type SubNavProps = {
  backgroundColor: BackgroundColor;
  links: { text: string }[];
  title: string;
};

const Styles = labelStyles('SubNav', {
  root: (backgroundColor: BackgroundColor) =>
    css({
      zIndex: 1,
      top: '0px',
      backgroundImage: `linear-gradient(90deg, ${Colors[backgroundColor]} 50%, rgba(0,0,0,0) 50%)`,
      position: 'sticky',
      boxShadow: `-50px 50px 30px ${Colors.neutral100}`,
      [MEDIA_MAX_WIDTH.large]: {
        position: 'relative',
        boxShadow: 'none',
      },
    }),
  linksContainer: css({
    zIndex: 1,
    width: 'fit-content',
    top: '0px',
    display: 'flex',
    height: '31px',
    [MEDIA_MAX_WIDTH.large]: {
      display: 'none',
    },
  }),
  title: css({
    ...Typography.pageHeader5,
    color: Colors.earth,
    paddingBottom: '26px',
    paddingTop: '87px',
    [MEDIA_MAX_WIDTH.medium]: {
      ...Typography.pageHeader9,
      paddingTop: '38px',
    },
    [MEDIA_MAX_WIDTH.small]: {
      ...Typography.pageHeader9,
      paddingTop: '48px',
    },
  }),
  link: (isActive: boolean) =>
    css({
      marginRight: '18px',
      ...Typography.paragraph3,
      color: 'currentColor',
      '&:focus': {
        color: Colors.blue,
      },

      '&:focus-visible': {
        color: Colors.blue,
      },

      '&:focus:not(:focus-visible)': {
        color: Colors.neutral800,
      },

      textDecoration: 'none',
      outline: '0',
      paddingTop: '6px',
      borderTop: `3px solid ${isActive ? Colors.zapier : 'transparent'}`,
    }),
});

export function SubNav({ backgroundColor, title, links }: SubNavProps) {
  const { scrollToSection, sectionIndex, subNavRef } =
    useAnimationsSectionContext();
  const { emitUserInteractionEvent } = useAvroTracking();

  return (
    <div
      css={Styles.root(backgroundColor)}
      data-testid="sub-nav"
      ref={subNavRef}
    >
      <Fragment>
        <div css={Styles.title}>
          <Heading>{title}</Heading>
        </div>
        <nav css={Styles.linksContainer}>
          {links.map(({ text }, index) => {
            const isActive = sectionIndex === index;
            const href = `#${text.toLowerCase().replace(' ', '-')}`;
            return (
              <a
                css={Styles.link(isActive)}
                href={href}
                key={`${text}-${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  // TO_DO - replace this JS scrolling with native <a /> scroll behavior using CSS scroll-behavior: smooth; to retain the smooth scroll effect.
                  scrollToSection(index);
                  emitUserInteractionEvent({
                    event_action: EventAction.click,
                    interaction_name: InteractionName.navigation,
                    interaction_raw_text: text,
                    option_selected: 'nav_link',
                    destination_href: href,
                    custom_tracking_fields: {
                      section_name: SectionName.AnimationsSection,
                    },
                  });
                }}
              >
                {text}
              </a>
            );
          })}
        </nav>
      </Fragment>
    </div>
  );
}
